
import { useState, useEffect } from 'react';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  image: string;
  rating: number;
  text: string;
  plan: string;
}

export default function TestimonialGrid() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('testimonial-grid');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      role: 'Individual Plan Client',
      image: 'https://public.readdy.ai/ai/img_res/e4fcf20aea597ae85e7191bc63f9605f.jpg',
      rating: 5,
      text: 'After struggling to find affordable health insurance on my own, I contacted Dallas Health Insurance Experts. They found me a plan that saved me $200 per month with better coverage! Their knowledge of the Dallas market is unmatched.',
      plan: 'Individual Health Plan'
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'Small Business Owner',
      image: 'https://readdy.ai/api/search-image?query=asian-american-businessman-in-his-forties-smiling-warmly-wearing-professional-business-attire-confident-friendly-demeanor-modern-office-setting-natural-lighting&width=400&height=400&seq=testimonial-client-002&orientation=squarish',
      rating: 5,
      text: 'Setting up group health insurance for my 15 employees seemed overwhelming until I met with this team. They handled everything from carrier selection to enrollment, and my employees love their coverage. Highly recommend!',
      plan: 'Group Health Insurance'
    },
    {
      id: 3,
      name: 'Linda Rodriguez',
      role: 'Medicare Client',
      image: 'https://readdy.ai/api/search-image?query=hispanic-woman-in-her-sixties-smiling-joyfully-wearing-casual-elegant-clothing-warm-friendly-expression-bright-natural-lighting-comfortable-home-setting&width=400&height=400&seq=testimonial-client-003&orientation=squarish',
      rating: 5,
      text: 'Navigating Medicare was confusing until I found Dallas Health Insurance Experts. They explained all my options clearly and helped me choose the perfect Medicare Supplement plan. I feel secure knowing they\'re always available to answer my questions.',
      plan: 'Medicare Supplement'
    },
    {
      id: 4,
      name: 'James Patterson',
      role: 'Family Plan Client',
      image: 'https://readdy.ai/api/search-image?query=professional-man-in-his-late-thirties-smiling-warmly-wearing-smart-casual-attire-friendly-approachable-expression-bright-natural-lighting-modern-home-office&width=400&height=400&seq=testimonial-client-004&orientation=squarish',
      rating: 5,
      text: 'With three kids and my wife expecting our fourth, finding comprehensive family coverage was critical. They compared plans from multiple carriers and found us excellent coverage at a price that fits our budget. True professionals!',
      plan: 'Family Health Plan'
    },
    {
      id: 5,
      name: 'Rebecca Thompson',
      role: 'Self-Employed Professional',
      image: 'https://readdy.ai/api/search-image?query=professional-woman-in-her-forties-smiling-confidently-wearing-business-professional-attire-warm-friendly-demeanor-bright-natural-lighting-modern-workspace&width=400&height=400&seq=testimonial-client-005&orientation=squarish',
      rating: 5,
      text: 'As a freelance consultant, I needed flexible health insurance that wouldn\'t break the bank. The team found me a plan with great coverage and a network that includes all my preferred doctors. Their service is exceptional!',
      plan: 'Individual Health Plan'
    },
    {
      id: 6,
      name: 'David Martinez',
      role: 'Business Owner',
      image: 'https://readdy.ai/api/search-image?query=hispanic-businessman-in-his-fifties-smiling-warmly-wearing-professional-business-suit-confident-friendly-expression-bright-natural-lighting-modern-office&width=400&height=400&seq=testimonial-client-006&orientation=squarish',
      rating: 5,
      text: 'We\'ve been working with Dallas Health Insurance Experts for 5 years now. They handle our company\'s group insurance and always find us competitive rates during renewal. Their customer service is outstanding!',
      plan: 'Group Health Insurance'
    },
    {
      id: 7,
      name: 'Emily Watson',
      role: 'Young Professional',
      image: 'https://readdy.ai/api/search-image?query=young-professional-woman-in-her-twenties-smiling-brightly-wearing-modern-casual-business-attire-energetic-friendly-expression-bright-natural-lighting-contemporary-office&width=400&height=400&seq=testimonial-client-007&orientation=squarish',
      rating: 5,
      text: 'Just graduated and needed my own health insurance for the first time. The team was so patient explaining everything and helped me find affordable coverage that meets my needs. They made the process stress-free!',
      plan: 'Individual Health Plan'
    },
    {
      id: 8,
      name: 'Robert Johnson',
      role: 'Retiree',
      image: 'https://readdy.ai/api/search-image?query=senior-man-in-his-late-sixties-smiling-warmly-wearing-casual-comfortable-clothing-friendly-wise-expression-bright-natural-lighting-comfortable-home-setting&width=400&height=400&seq=testimonial-client-008&orientation=squarish',
      rating: 5,
      text: 'Retiring early meant I needed coverage until Medicare eligibility. Dallas Health Insurance Experts found me a bridge plan that covered everything I needed. Their expertise saved me thousands of dollars!',
      plan: 'Short-Term Health Insurance'
    },
    {
      id: 9,
      name: 'Amanda Foster',
      role: 'Family Plan Client',
      image: 'https://readdy.ai/api/search-image?query=professional-woman-in-her-mid-thirties-smiling-joyfully-wearing-casual-elegant-attire-warm-friendly-expression-bright-natural-lighting-modern-home-environment&width=400&height=400&seq=testimonial-client-009&orientation=squarish',
      rating: 5,
      text: 'When my husband changed jobs, we needed new family coverage quickly. The team worked fast to compare options and had us enrolled within days. No gap in coverage and better benefits than before!',
      plan: 'Family Health Plan'
    }
  ];

  return (
    <section id="testimonial-grid" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-darkGray max-w-3xl mx-auto">
            Real stories from Dallas families and businesses who trust us with their health insurance needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`bg-lightBlue/5 rounded-xl p-8 border border-lightBlue/20 hover:shadow-xl transition-all duration-500 ${
                isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-semibold text-lg text-primary">
                    {testimonial.name}
                  </h3>
                  <p className="text-sm text-darkGray">{testimonial.role}</p>
                </div>
              </div>

              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-secondary text-lg"></i>
                ))}
              </div>

              <p className="text-darkGray leading-relaxed mb-4">
                "{testimonial.text}"
              </p>

              <div className="pt-4 border-t border-lightBlue/20">
                <div className="flex items-center gap-2 text-sm text-secondary font-medium">
                  <i className="ri-shield-check-line"></i>
                  <span>{testimonial.plan}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
